import { useEffect, useState } from 'react';
import type { ClientConfig } from '@archipro-website/config';
import { useRootData } from '@modules/root';
import { FacebookPixelsHead } from './FacebookTracker';
import { LinkedInTrackerHead } from './LinkedInTracker';
import { PinterestTrackerHead } from './PinterestTracker';
import { OutbrainTrackerHead } from './Outbrain';
import { shouldAllowAdvertisingPixels } from './allow-advertising-pixels';
import { isConfiguredPixelId } from './is-configured-pixel-id';

interface NavigatorWithGPC extends Navigator {
    globalPrivacyControl?: boolean;
}

/**
 * Injects the `<script>` halves of advertising trackers on the client only,
 * after checking the Global Privacy Control browser signal. The matching
 * `<noscript><img>` halves are SSR-rendered in `BodyPixels` and honour the HTTP
 * `Sec-GPC` header when the market requires it.
 *
 * `allowAdvertising` starts false and resolves in `useEffect` so GPC is read
 * from `navigator` after hydration (never during SSR). Advertising scripts
 * therefore load one tick later than when they lived in `HeadPixels`. Outbrain
 * has no `<noscript>` fallback, so no-JS requests get no Outbrain coverage;
 * Facebook, Pinterest, and LinkedIn still emit `<noscript><img>` pixels from
 * `BodyPixels`.
 */
export const ClientAdvertisingPixels = ({
    facebookPixelArchiproId,
    facebookPixelClientId,
    pinterestTagArchiproId,
    pinterestTagClientId,
    linkedinPartnerId,
    outbrainId,
}: Pick<
    ClientConfig['pixels'],
    | 'facebookPixelArchiproId'
    | 'facebookPixelClientId'
    | 'pinterestTagArchiproId'
    | 'pinterestTagClientId'
    | 'linkedinPartnerId'
    | 'outbrainId'
>) => {
    const { localisation } = useRootData();
    const { honourGlobalPrivacyControl } = localisation.countryConfig;
    const [allowAdvertising, setAllowAdvertising] = useState(false);

    useEffect(() => {
        const gpcEnabled =
            (navigator as NavigatorWithGPC).globalPrivacyControl === true;
        setAllowAdvertising(
            shouldAllowAdvertisingPixels(
                honourGlobalPrivacyControl,
                gpcEnabled,
            ),
        );
    }, [honourGlobalPrivacyControl]);

    if (!allowAdvertising) {
        return null;
    }

    const hasFacebookPixel =
        isConfiguredPixelId(facebookPixelArchiproId) ||
        isConfiguredPixelId(facebookPixelClientId);
    const hasPinterestPixel =
        isConfiguredPixelId(pinterestTagArchiproId) ||
        isConfiguredPixelId(pinterestTagClientId);

    return (
        <>
            {hasFacebookPixel && (
                <FacebookPixelsHead
                    pixel={facebookPixelArchiproId}
                    pixelClient={facebookPixelClientId}
                />
            )}
            {hasPinterestPixel && (
                <PinterestTrackerHead
                    pixelArchipro={pinterestTagArchiproId}
                    pixelClient={pinterestTagClientId}
                />
            )}
            {isConfiguredPixelId(linkedinPartnerId) && (
                <LinkedInTrackerHead partnerId={linkedinPartnerId} />
            )}
            {isConfiguredPixelId(outbrainId) && (
                <OutbrainTrackerHead id={outbrainId} />
            )}
        </>
    );
};
